import { Icon, IconName, IconProps } from '@clarion/ClarionIcon';
import { ReactNode } from 'react';
import { Heading } from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { cx } from '../util';

export type ClarionCalloutProps = React.HTMLAttributes<HTMLDivElement> & {
  /** The content to display inside the callout */
  children?: ReactNode;
  /** Visual intent color to apply to element */
  intent?: 'none' | 'primary' | 'success' | 'warning' | 'danger';
  /** Icon to render before the children */
  icon?: IconProps['icon'];
  /** The title of the callout */
  title?: string;
  /** Additional CSS class name(s) to apply to the callout */
  className?: string;
};

const intentIcons: Record<string, IconName | null> = {
  none: null,
  primary: 'info-sign',
  success: 'tick',
  warning: 'warning-sign',
  danger: 'error',
};

const calloutStyles = tv({
  base: cx(
    'rounded-xl p-4 bg-neutral-100 dark:bg-neutral-800 forced-colors:border-[ButtonBorder] forced-colors:border',
  ),
  variants: {
    intent: {
      none: '',
      primary: 'bg-purple-400/10 dark:bg-purple-400/20 text-purple-900 dark:text-purple-100',
      success: 'bg-green-400/10 dark:bg-green-400/20 text-green-900 dark:text-green-100',
      warning: 'bg-orange-400/10 dark:bg-orange-400/20 text-orange-900 dark:text-orange-100',
      danger: 'bg-red-400/10 dark:bg-red-400/20 text-red-900 dark:text-red-100',
    },
  },
});

export function ClarionCallout({
  children,
  intent = 'none',
  icon,
  title,
  className,
  ...props
}: ClarionCalloutProps) {
  return (
    <div {...props} className={cx(calloutStyles({ intent }), className)}>
      <div className="flex gap-3">
        {icon !== null && (
          <Icon
            icon={icon || intentIcons[intent]}
            className={cx(title ? 'mt-1' : 'mt-px', 'shrink-0')}
          />
        )}
        <div className="text-sm">
          {title && (
            <Heading slot="title" className="text-base font-semibold">
              {title}
            </Heading>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
